"use client"

import styled from "@emotion/styled"
import { Box, Button } from "@mui/material"
import { Image } from "next/dist/client/image-component"
import { useEffect, useRef, useState } from "react"
import { Autoplay, Mousewheel, Pagination, Scrollbar } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { Desktop, Mobile, NewText } from "ui"
import { extractProperty, getUmbracoMediaUrl } from "utils"

const StyledSliderWrapper = styled.div<{ $stripeHeight: number }>`
  position: relative;
  width: 100vw;
  height: 100dvh;
  overflow: hidden;
  .swiper {
    height: 100%;
  }
  .swiper-slide {
    height: auto;
  }
  .swiper-pagination {
    bottom: ${(p) => p.$stripeHeight + 16}px !important;
    left: 50% !important;
    transform: translateX(-50%);
    width: fit-content !important;
    padding: 4px;
    background: white;
    border-radius: 200px;
    display: flex;
    gap: 8px;
    ${(props) => props.theme.breakpoints.up("md")} {
      bottom: 60px !important;
      padding: 6px 8px;
      gap: 16px;
    }
    .swiper-pagination-bullet {
      background: #cec2b8;
      opacity: 1;
      border-radius: 466.67px;
      height: 8px;
      width: 8px;
      transition: width 300ms ease-in-out;
      margin: 0 !important;
      ${(props) => props.theme.breakpoints.up("md")} {
        height: 14px;
        width: 14px;
      }
    }
    .swiper-pagination-bullet-active {
      width: 37px;
      background: #1d1d1b;
      ${(props) => props.theme.breakpoints.up("md")} {
        width: 65px;
      }
    }
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 100%;
  }
`
const StyledImageWrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`

const StyledTextWrapper = styled.div<{ $shiftDown?: boolean }>`
  position: absolute;
  top: ${(props) => (props.$shiftDown ? "100px" : "0")};
  left: 0;
  min-height: 453px;
  max-height: 100%;
  padding: 71px 63px;
  width: 100%;
  z-index: 9;
  p {
    margin: 0;
  }
  ${(props) => props.theme.breakpoints.up("sm")} {
    top: 5vw;
    padding: 0 7vw;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    padding: 0 7vw;
    min-height: 100%;
  }
  @media screen and (min-width: 1900px) {
    padding: 0 10vw;
  }
`
const StyledBottomText = styled(NewText)`
  position: absolute;
  bottom: 60px;
  left: 0;
  padding: 3px 20px 1px 28px;
  background: rgba(0, 0, 0, 0.8);
`

const StyledBottomTextWrapper = styled.div`
  padding: 23px 31px 16px 16px;
  p {
    margin: 0;
  }
`

const StyledButton = styled(Button)`
  font-size: 18.79px;
  text-decoration: unset;
  width: fit-content;
  height: 32px;
  padding: 0px 18px;
  background-color: #000000;
  border: unset;
  color: #ffffff;
  border-radius: 12.65px;
  letter-spacing: -0.01em;
  :hover {
    background-color: #000000;
  }
`

type Props = {
  locale: string
  data: any
  companyId?: number
  companyName?: string
  messages?: any
  scrollDownToForm: () => void
}

type SliderType = {
  imageForDesktop: string
  imageForMobile: string
  title: string
  description: string
  imageCredit: string
  backgroundColorForMobile: string
  whiteColor: boolean
  hideContentFromDesktop: boolean
}

export const LandingSlider: React.FC<Props> = ({ data, scrollDownToForm }: Props) => {
  const imageSlider: SliderType[] = extractProperty(data, "imageSkuder")?.map((image: any) => {
    const imageForDesktop = extractProperty(image, "imageForDesktop")
    const imageForMobile = extractProperty(image, "imageForMobile")
    const title = extractProperty(image, "title")
    const description = extractProperty(image, "description")
    const imageCredit = extractProperty(image, "imageCredit")
    const backgroundColorForMobile = extractProperty(image, "backgroundColorForMobile")
    const whiteColor = image?.properties?.find(
      (property: any) => property?.propertyName === "whiteColor",
    )?.propertyValue?.value
    const hideContentFromDesktop = image?.properties?.find(
      (property: any) => property?.propertyName === "hideContentFromDesktop",
    )?.propertyValue?.value

    return {
      imageForDesktop,
      imageForMobile,
      title,
      description,
      imageCredit,
      backgroundColorForMobile,
      whiteColor,
      hideContentFromDesktop,
    }
  })
  const formButton = extractProperty(data, "formButton")?.[0]?.name

  const bottomRefs = useRef<(HTMLDivElement | null)[]>([])
  const [bottomHeights, setBottomHeights] = useState<number[]>([])
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    if (!bottomRefs.current) return

    const newHeights = bottomRefs.current.map((ref) => ref?.offsetHeight || 0)
    setBottomHeights(newHeights)
  }, [imageSlider.length])

  return (
    <StyledSliderWrapper $stripeHeight={bottomHeights[activeIndex]}>
      <Swiper
        style={{
          padding: 0,
          height: "100%",
          width: "100%",
        }}
        direction="horizontal"
        spaceBetween={0}
        modules={[Scrollbar, Mousewheel, Pagination, Autoplay]}
        onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
        scrollbar={{ draggable: true }}
        mousewheel={true}
        slidesPerView={1}
        initialSlide={0}
        pagination={{
          clickable: true,
          renderBullet: (index, className) => {
            return `<span class="${className}"></span>`
          },
        }}
      >
        {imageSlider.map((item: SliderType, key: number) => (
          <SwiperSlide
            key={key.toString()}
            style={{
              width: "100%",
            }}
          >
            <Desktop sx={{ height: "100%" }}>
              <StyledImageWrapper>
                {!item.hideContentFromDesktop && (
                  <StyledTextWrapper $shiftDown={key === 2}>
                    <NewText
                      component={"h2"}
                      desktopFontSize={60}
                      desktopFontWeight={700}
                      desktopLineHeight={"110%"}
                      letterSpacing={"-0.01em"}
                      color="white"
                    >
                      {item.title}
                    </NewText>
                    <NewText
                      component={"p"}
                      desktopFontSize={60}
                      desktopFontWeight={500}
                      desktopLineHeight={"110%"}
                      letterSpacing={"-0.02em"}
                      color="white"
                      dangerouslySetInnerHTML={{
                        __html: item.description,
                      }}
                    />
                  </StyledTextWrapper>
                )}
                <Image
                  src={getUmbracoMediaUrl(item?.imageForDesktop)}
                  fill
                  style={{
                    height: "100%",
                    width: "100%",
                    objectFit: "cover",
                    objectPosition: "top",
                  }}
                  alt={item.imageCredit}
                  priority={key === 0}
                />
                <StyledBottomText
                  component={"span"}
                  desktopFontSize={14}
                  desktopLineHeight={"13px"}
                  desktopFontWeight={600}
                  letterSpacing={"0"}
                  color="white"
                >
                  הדמיות להמחשה בלבד. טל.ח.
                </StyledBottomText>
              </StyledImageWrapper>
            </Desktop>
            <Mobile sx={{ height: "100dvh" }}>
              <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <Box
                  sx={{
                    height: `calc(100% - ${bottomHeights[key] || 0}px)`,
                    transition: "height 0.3s ease",
                    position: "relative",
                  }}
                >
                  <Image
                    src={getUmbracoMediaUrl(item?.imageForMobile)}
                    fill
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "top",
                    }}
                    alt={item.imageCredit}
                    priority={key === 0}
                  />
                </Box>
                <StyledBottomTextWrapper
                  ref={(el) => (bottomRefs.current[key] = el)}
                  style={{ backgroundColor: `#${item.backgroundColorForMobile}` }}
                >
                  {(item.title || item.description) && (
                    <Box
                      marginBottom={"16px"}
                      maxWidth={"273px"}
                      sx={{
                        color: item.whiteColor ? "#ffffff" : "#000000",
                      }}
                    >
                      <NewText
                        component={"h2"}
                        desktopFontSize={25.79}
                        desktopFontWeight={700}
                        desktopLineHeight={"22.2px"}
                        letterSpacing={"-0.05em"}
                        inheritColor
                      >
                        {item.title}
                      </NewText>
                      <NewText
                        component={"p"}
                        desktopFontSize={25.79}
                        desktopFontWeight={500}
                        desktopLineHeight={"22.2px"}
                        letterSpacing={"-0.05em"}
                        inheritColor
                        dangerouslySetInnerHTML={{
                          __html: item.description,
                        }}
                        marginBottom={"16px"}
                      />
                    </Box>
                  )}
                  <StyledButton onClick={scrollDownToForm}>{formButton}</StyledButton>
                </StyledBottomTextWrapper>
              </Box>
            </Mobile>
          </SwiperSlide>
        ))}
      </Swiper>
    </StyledSliderWrapper>
  )
}

export default LandingSlider
